import { useMemo } from "react";

import { useQuery } from "@apollo/client";

import { RecordingsApiResponse } from "../../../../common/types/recordings/api";
import { GetRecordingsByEventParams } from "../../../../common/types/recordings/params";
import { buildEventQueryVariables } from "../../../../common/utils/buildEventQueryVariables";
import { DEFAULT_LIMIT_COUNT } from "../../../../common/variables/common";
import { errorNotification } from "../../../../common/variables/notification";
import { GET_RECORDINGS_BY_EVENT } from "../../../../rest/queries";
import { mapRecordingEntityToViewModel } from "../../adapters/recordingAdapter";
import { RecordingViewModel } from "../../models/recordingViewModel";
import { handleApolloError } from "../../../../common/utils/handleApolloError";

export const useGetRecordingsByEvent = (params: GetRecordingsByEventParams) => {
  const skip = !params.customerId || !params.eventTimestamp;

  const variables = buildEventQueryVariables(params);

  const { data, loading, error, refetch, fetchMore } = useQuery<{
    recordings: RecordingsApiResponse;
  }>(GET_RECORDINGS_BY_EVENT, {
    variables,
    skip,
    fetchPolicy: "cache-first",
    onError: handleApolloError,
  });

  const recordings = useMemo<RecordingViewModel[]>(() => {
    const recordings =
      data?.recordings?.data.map(mapRecordingEntityToViewModel) ?? [];

    return recordings;
  }, [data]);

  const lastEvaluatedKey = data?.recordings?.lastEvaluatedKey;

  const fetchNextPage = async () => {
    try {
      let currentLastEvaluatedKey = lastEvaluatedKey;
      let allNewRecordings: RecordingViewModel[] = [];

      // Continue fetching until there are no more pages
      while (currentLastEvaluatedKey) {
        const response = await fetchMore({
          variables: {
            ...variables,
            pageSize: DEFAULT_LIMIT_COUNT,
            lastEvaluatedKey: encodeURIComponent(
              JSON.stringify(currentLastEvaluatedKey)
            ),
          },
          updateQuery: (prev, { fetchMoreResult }) => {
            if (!fetchMoreResult) return prev;

            return {
              ...fetchMoreResult,
              recordings: {
                ...fetchMoreResult.recordings,
                data: [
                  ...(prev.recordings?.data ?? []),
                  ...(fetchMoreResult.recordings?.data ?? []),
                ],
                lastEvaluatedKey: fetchMoreResult.recordings?.lastEvaluatedKey,
              },
            };
          },
        });

        const newRecordings =
          response.data?.recordings?.data.map(mapRecordingEntityToViewModel) ??
          [];

        allNewRecordings = [...allNewRecordings, ...newRecordings];

        currentLastEvaluatedKey = response.data?.recordings?.lastEvaluatedKey;
      }
    } catch (error) {
      errorNotification("Error fetching recordings");

      console.error("Error fetching recordings:", error);
    }
  };

  return {
    recordings,
    loading,
    error,
    refetch,
    fetchMore,
    lastEvaluatedKey,
    fetchNextPage,
  } as const;
};
